// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const ASK_BASE_URL = import.meta.env.VITE_BASE_ASK_URL;
const ASK_API_ENDPOINT = '/api/AskSession';
const ASK_LITIUM_SCOPE =
  'api://c98b7623-f120-463e-9a81-42e287c0ab58/access_as_user';

const HEADERS = {};

// ============= Types =============

export interface DeleteAskSessionProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  askSessionId: string;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Delete an AskSession
 * @param getAccessToken Function to acquire access token
 * @param askSessionId Ask Session identifier
 * @returns Promise that resolves when deletion is complete or rejects on error
 */
export const deleteAskSession = async ({
  getAccessToken,
  askSessionId,
}: DeleteAskSessionProps): Promise<void> => {
  try {
    console.log('🔄 Deleting Ask Session...');

    // Token acquisition with required scopes
    const token = await getAccessToken([ASK_LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${ASK_BASE_URL}${ASK_API_ENDPOINT}/${askSessionId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'DELETE',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    console.log('✅ Ask Session deleted successfully');
  } catch (error) {
    console.error('🔴 Error deleting Ask Session:', error);
    throw error;
  }
};
